// Detects invalid observed marker placement in resolved selectors.
// Example: `.host? .child` should warn because the marker is not on the tail.
import { resolveObservedSelectorPart } from './observed-selector'
import { splitTopLevelSelectorList } from './selector-list'

// Build a warning message when a selector contains misplaced observed markers.
export function findInvalidObservedSelectorMessage(selector: string): string | null {
  const invalidPart = findInvalidObservedSelectorPart(selector)
  if (!invalidPart) {
    return null
  }

  // Return one stable warning message for invalid observed placement.
  return `Observed marker "?" or "?!" must be attached to the final selector tail. Descendant chains place the marker on the final target element, for example ".host .child?" not ".host? .child". Selector: "${invalidPart}".`
}

// Find the first selector-list part with invalid observed marker syntax.
function findInvalidObservedSelectorPart(selector: string): string | null {
  for (const part of splitTopLevelSelectorList(selector)) {
    const resolution = resolveObservedSelectorPart(part)
    if (resolution.observedMode !== 'none') continue
    if (!containsObservedMarkerCandidate(part)) continue
    return part.trim()
  }

  return null
}

// Detect unescaped `?` markers outside attribute selectors and quoted text.
function containsObservedMarkerCandidate(selector: string): boolean {
  let quote: '"' | "'" | null = null
  let bracketDepth = 0

  // Scan the selector once and ignore quoted attribute content.
  for (let index = 0; index < selector.length; index += 1) {
    const char = selector[index]
    const previous = index > 0 ? selector[index - 1] : ''

    // Keep quoted content opaque until the matching quote closes.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue quoted-text scan.
      }
      continue
    } else {
      // Continue non-quoted selector scan.
    }

    // Track attribute selector brackets like `[href*="?"]`.
    if (char === '[') {
      bracketDepth += 1
      continue
    } else if (char === ']' && bracketDepth > 0) {
      bracketDepth -= 1
      continue
    } else if (bracketDepth > 0 && (char === '"' || char === "'")) {
      quote = char
      continue
    } else {
      // Continue regular marker detection outside attributes.
    }

    // Return true for an unescaped observed marker candidate.
    if (char === '?' && previous !== '\\' && bracketDepth === 0) {
      return true
    } else {
      // Keep scanning remaining selector characters.
    }
  }

  // Return false when selector has no observed marker candidate.
  return false
}
